import { useEffect, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useNavigate } from 'react-router-dom';
import { api } from './lib/api';
import { VERSION, COPYRIGHT_YEAR, COPYRIGHT_COMPANY } from './lib/version';
import { Login } from './pages/Login';
import { Forgot } from './pages/Forgot';
import { Reset } from './pages/Reset';
import { Dashboard } from './pages/Dashboard';
import { Billing } from './pages/Billing';
import { Products } from './pages/Products';
import { StripeEvents } from './pages/StripeEvents';
import { AdminUsers } from './pages/AdminUsers';
import { Audit } from './pages/Audit';
import { Settings } from './pages/Settings';
import { TenantDetail } from './pages/TenantDetail';

interface MeUser {
  id: number;
  email: string;
  role: 'admin' | 'support' | 'readonly';
}

export function App() {
  const [me, setMe] = useState<MeUser | null | undefined>(undefined);
  const navigate = useNavigate();

  async function refresh() {
    try {
      const data = await api<{ user: MeUser }>('/auth/me');
      setMe(data.user);
    } catch {
      setMe(null);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  if (me === undefined) {
    return <div style={{ padding: 40 }}>Loading…</div>;
  }

  if (me === null) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={() => refresh()} />} />
        <Route path="/forgot" element={<Forgot />} />
        <Route path="/reset" element={<Reset onReset={() => refresh()} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  async function logout() {
    await api('/auth/logout', { method: 'POST' });
    setMe(null);
    navigate('/login');
  }

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">Dealer Bosses</div>
        <nav>
          <NavLink to="/" end>Tenants</NavLink>
          <NavLink to="/products">Products</NavLink>
          <NavLink to="/billing">Billing</NavLink>
          <NavLink to="/stripe-events">Stripe events</NavLink>
          <NavLink to="/admin-users">Platform admins</NavLink>
          <NavLink to="/audit">Audit log</NavLink>
          <NavLink to="/settings">Settings</NavLink>
        </nav>
        <div className="footer">
          <div className="muted small">{me.email}</div>
          <div style={{ marginTop: 6 }}>
            <button onClick={logout}>Sign out</button>
          </div>
          <div className="muted small" style={{ marginTop: 12, fontSize: 11, lineHeight: 1.4 }}>
            {VERSION} &middot; &copy; {COPYRIGHT_YEAR} <a href="https://llnet-inc.ca" target="_blank" rel="noopener noreferrer">{COPYRIGHT_COMPANY}</a>
          </div>
        </div>
      </aside>
      <main className="main">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/tenants/:id" element={<TenantDetail />} />
          <Route path="/products" element={<Products />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/stripe-events" element={<StripeEvents />} />
          <Route path="/admin-users" element={<AdminUsers />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
